import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgIf, NgStyle } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertController, ModalController, IonicModule } from '@ionic/angular';
import { FormHelper } from 'src/app/core/helpers/form.helper';
import { ReportConfig } from 'src/app/core/interfaces/report-config';
import { ToastrService } from 'ngx-toastr';
import { ResourceService } from 'src/app/core/services/resource.service';
import { filter, throwError } from 'rxjs';
import { ResourcesEndpointEnum } from 'src/app/core/enums/resources-endpoint.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FrontendConfig } from 'src/app/core/interfaces/frontend-config';
import { ConfigurationStore } from 'src/app/core/stores/configuration.store';

@Component({
  selector: 'app-report-modal',
  templateUrl: './report-modal.component.html',
  styleUrls: ['./report-modal.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    ReactiveFormsModule,
    NgIf,
    NgStyle,
    TranslateModule
  ],
})
export class ReportModalComponent implements OnInit {
  @Input() report?: ReportConfig;
  @Input() groupID?: number;
  @Input() groupImg?: string;
  configuration?: FrontendConfig;
  responseNewLogo: any;

  file: File = null;
  reportForm!: FormGroup;
  reportFormProcessing = false;
  reportFormSubmitted = false;
  usesSSASEIdUsername = false;
  hasRLS = false;
  hasRLSType = false;
  formHelper = FormHelper;

  constructor(public modalController: ModalController,
    private configurationStore: ConfigurationStore,
    private resourceService: ResourceService,
    private toastrService: ToastrService,
    private alertController: AlertController,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private formBuilder: FormBuilder) { }

  ngOnInit() {
    this.getConfiguration();
    this.hasRLS = this.report.RLS ? this.report.RLS : false;
    this.hasRLSType = this.report?.RLSRole?.eIdType ? true : false;
    this.buildForm();
  }

  buildForm() {
    this.reportForm = this.formBuilder.group({
      ReportID: [this.report.ReportID],
      Title: [this.report ? this.report.Title : null, [Validators.required, Validators.maxLength(55)]],
      ReportGUID: [this.report ? this.report.ReportGUID : null, [Validators.required]],
      ReportType: this.formBuilder.group({
        ReportTypeID: [this.report.ReportType.ReportTypeID],
        ReportType: [this.report.ReportType.ReportType]
      }),
      Description: [this.report ? this.report.Description : null],
      MobileFriendly: [this.report ? this.report.MobileFriendly : null],
      FilterMenu: [this.report.FilterMenu ? this.report.FilterMenu : null],
      PageBar: [this.report.PageBar ? this.report.PageBar : null],
      BookmarkPane: [this.report.BookmarkPane ? this.report.BookmarkPane : null],
      RLS: [this.report ? (this.report.RLS ? this.report.RLS : false) : null],
      RLSRole: this.formBuilder.group({
        eIdType: this.formBuilder.group({
          eIdTypeID: [this.report ? (this.report.RLSRole ? this.report.RLSRole.eIdType.eIdTypeID : 0) : 0],
          eIdTypeName: [this.report ? (this.report.RLSRole ? this.report.RLSRole.eIdType.eIdTypeName : "Email") : "Email"]
        }),
        eIdInput: [this.report ? (this.report.RLSRole ? this.report.RLSRole.eIdInput : "") : ""]
      }),
      // RLSRoleName: [this.report ? (this.report.RLSRoleName ? this.report.RLSRoleName : "") : null],
      // RLSFilteredOnEmail: [this.report ? (this.report.RLSFilteredOnEmail ? this.report.RLSFilteredOnEmail : false) : false],
      Published: [this.report ? this.report.Published : null, [Validators.required]],
      Pinned: [this.report ? this.report.Pinned : null],
      AllowExport: [this.report?.AllowExport ?? true],
      ImageURI: [this.report ? this.report.ImageURI : null],
    });
  }

  getConfiguration() {
    this.configurationStore.config$.pipe(
      filter((config: FrontendConfig | null) => config !== null))
      .subscribe({
        next: (config: FrontendConfig | null) => {
          this.configuration = config
        },
        error: (err) => {
          throwError(() => err);
        }
      });
  }

  uploadLogo(event) {
    this.file = event.target.files[0]

    if (this.file) {
      if (this.isSVGLogo(this.file)) {
        this.translateService.get('Logo File Format should be png, jpeg, jpg').subscribe(t => {
          this.toastrService.error(t)
        })
      } else {
        this.resourceService.post<any>(ResourcesEndpointEnum.UPLOAD_REPORT_IMAGE, this.file, { groupID: this.groupID, reportID: this.report.ReportID, fileName: this.file.name })
          .subscribe({
            next: (response: any) => {
              this.responseNewLogo = response
              this.cdr.detectChanges();
              this.translateService.get('Report Image upload successful').subscribe(t => {
                this.toastrService.success(t);
              })
            },
            error: (err) => {
              this.translateService.get('Error when uploading Report Image').subscribe(t => {
                this.toastrService.error(t);
              })
              throwError(() => err);
            },
            complete: () => { }
          });
      }
    }
  }

  private isSVGLogo(file: any): boolean {
    let isSVG = false
    if (file.type.includes('svg')) {
      return isSVG = true
    }
    return isSVG
  }

  enabledRLS(event: any) {
    if (event.detail.checked)
      this.hasRLS = true
    else
      this.hasRLS = false
  }

  protected RLSTypeChanged(event: CustomEvent) {
    this.hasRLSType = true;
    if(event.detail.value === 4){
      this.usesSSASEIdUsername = true
    }
    else{
      this.usesSSASEIdUsername = false
    }
  }

  saveReport(): void {
    this.reportFormProcessing = true;
    this.reportFormSubmitted = true;
    if (this.reportForm.invalid) {
      this.reportFormProcessing = false;
      return;
    }

    const reportForm = this.reportForm.value;
    reportForm.ImageURI = this.responseNewLogo ? this.responseNewLogo.ImageURI : this.report.ImageURI;
    void this.modalController.dismiss({
      reportForm,
      action: this.report ? 'edit' : 'create'
    });
  }

  saveAndOpenEmbedding(){
    this.resourceService.put<ReportConfig>(ResourcesEndpointEnum.PUT_REPORT, this.reportForm.value, { groupID: this.groupID, reportID: this.report.ReportID })
      .subscribe({
        next: (report: ReportConfig) => {
          this.cdr.detectChanges();
          this.translateService.get('Report Info edited successfully').subscribe(t => {
            this.toastrService.success(t);
          })
        },
        error: (err: any) => {
          if (err.status === 400) {
            this.translateService.get('Error editing report info').subscribe(t => {
              this.toastrService.error(err.error, t);
            })
          }
          else {
            this.translateService.get('Error editing report info').subscribe(t => {
              this.toastrService.error(t);
            })
          }
          throwError(() => err);
        },
        complete: () => {
          window.open(`home/reports/${this.report.ReportID}/${this.report.ReportType.ReportType}?id=${this.groupID}&test=true`, '_self');
        }
      });
  }

  testEmbedding(): void {
    this.translateService.get(['Test Embedding', 'Save and open report in same tab', 'Cancel', 'Continue']).subscribe(t => {
      this.alertController.create({
        header: t['Test Embedding'] + '?',
        cssClass: 'custom-alert',
        message: t['Save and open report in same tab'] + '<br>',
        buttons: [
          {
            text: t['Cancel'],
            cssClass: 'cancel-button',
            role: 'cancel'
          },
          {
            text: t['Continue'],
            cssClass: 'confirm-button',
            handler: () => {
              this.saveAndOpenEmbedding();
            }
          }
        ]
      }).then((alert) => {
        void alert.present();
      });
    })
  }

  onBack() {
    this.modalController.dismiss(false);
  }
}
